"use client";

import { Button } from "@heroui/button";
import { Divider } from "@heroui/divider";
import { Modal, ModalBody, ModalContent } from "@heroui/modal";
import { Switch } from "@heroui/switch";
import { zodResolver } from "@hookform/resolvers/zod";
import { InformationCircleIcon } from "@icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useHotkeys } from "react-hotkeys-hook";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { useWorkflowSelection } from "@/features/chat/hooks/useWorkflowSelection";
import { useIntegrations } from "@/features/integrations/hooks/useIntegrations";
import type { Integration } from "@/features/integrations/types";
import {
  MissingIntegrationsAlert,
  missingIntegrationsMessage,
} from "@/features/workflows/components/shared/WorkflowCardComponents";
import WorkflowDescriptionField from "@/features/workflows/components/workflow-modal/WorkflowDescriptionField";
import WorkflowFooter from "@/features/workflows/components/workflow-modal/WorkflowFooter";
import WorkflowHeader from "@/features/workflows/components/workflow-modal/WorkflowHeader";
import WorkflowLoadingState from "@/features/workflows/components/workflow-modal/WorkflowLoadingState";
import WorkflowRightPanel from "@/features/workflows/components/workflow-modal/WorkflowRightPanel";
import WorkflowStepsPreviewCard from "@/features/workflows/components/workflow-modal/WorkflowStepsPreviewCard";
import WorkflowTriggerSection from "@/features/workflows/components/workflow-modal/WorkflowTriggerSection";
import { useWorkflowCreation } from "@/features/workflows/hooks/useWorkflowCreation";
import { usePlatform } from "@/hooks/ui/usePlatform";
import { useRouter } from "@/i18n/navigation";
import { ANALYTICS_EVENTS, trackEvent } from "@/lib/analytics";
import { getUserHomeTimezone } from "@/lib/timezone";
import { toast } from "@/lib/toast";
import type { WorkflowDraftData } from "@/types/features/toolDataTypes";
import type { PublicWorkflowStep } from "@/types/features/workflowTypes";
import { type Workflow, workflowApi } from "../api/workflowApi";
import { REGENERATION_REASONS } from "../constants/regeneration";
import {
  getDefaultFormValues,
  type WorkflowFormData,
  workflowFormSchema,
  workflowToFormData,
} from "../schemas/workflowFormSchema";
import { useWorkflowModalStore } from "../stores/workflowModalStore";
import { useWorkflowsStore } from "../stores/workflowsStore";
import { useTriggerSchemas } from "../triggers/hooks/useTriggerSchemas";
import { createDefaultTriggerConfig } from "../triggers/registry";
import { hasValidTriggerName, isIntegrationTrigger } from "../triggers/types";
import { findTriggerSchema } from "../triggers/utils";
import { mentionedIntegrationIds } from "../utils/integrationMentions";

interface WorkflowModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onWorkflowSaved?: (workflowId: string) => void;
  onWorkflowDeleted?: (workflowId: string) => void;
  mode: "create" | "edit" | "preview";
  existingWorkflow?: Workflow | null;
  /** Pre-fill form from AI-generated draft data */
  draftData?: WorkflowDraftData | null;
  /**
   * Pre-built steps from a public/community workflow. When provided:
   * - the steps are forwarded to create so the backend skips regeneration
   * - the integration chip selector is hidden
   * - a read-only preview panel renders alongside the form
   */
  predefinedSteps?: PublicWorkflowStep[];
  /**
   * When true, the create button shows "Create and Send" and the workflow is
   * immediately executed in the chat after creation.
   */
  createAndSend?: boolean;
}

// Build the initial form values for a create-mode modal seeded from an
// AI-generated draft, normalizing its trigger into the form's shape.
function buildDraftFormValues(
  draftData: WorkflowDraftData,
  triggerSchemas: Parameters<typeof findTriggerSchema>[0],
): WorkflowFormData {
  const activeTab =
    draftData.trigger_type === "schedule"
      ? "schedule"
      : draftData.trigger_type === "integration"
        ? "trigger"
        : "manual";

  let triggerConfig: WorkflowFormData["trigger_config"];
  let selectedTriggerValue = "";

  if (draftData.trigger_type === "schedule") {
    triggerConfig = {
      type: "schedule" as const,
      enabled: true,
      cron_expression: draftData.cron_expression || "0 9 * * *",
      timezone: getUserHomeTimezone(),
    };
  } else if (
    draftData.trigger_type === "integration" &&
    draftData.trigger_slug
  ) {
    // Normalize trigger_slug: backend may return composio_slug, frontend needs slug
    const schema = findTriggerSchema(triggerSchemas, draftData.trigger_slug);
    const normalizedSlug = schema?.slug ?? draftData.trigger_slug;

    const defaultConfig = createDefaultTriggerConfig(normalizedSlug);
    if (defaultConfig) {
      triggerConfig = {
        ...defaultConfig,
        trigger_slug: normalizedSlug,
      };
    } else {
      triggerConfig = {
        type: normalizedSlug,
        enabled: true,
        trigger_name: normalizedSlug,
      };
    }
    selectedTriggerValue = normalizedSlug;
  } else {
    triggerConfig = {
      type: "manual" as const,
      enabled: true,
    };
  }

  return {
    title: draftData.suggested_title,
    description: draftData.suggested_description || undefined,
    prompt: draftData.prompt || draftData.suggested_description || "",
    activeTab,
    selectedTrigger: selectedTriggerValue,
    trigger_config: triggerConfig,
    notify_on_completion: true,
  };
}

export default function WorkflowModal({
  isOpen,
  onOpenChange,
  onWorkflowSaved,
  onWorkflowDeleted,
  mode,
  existingWorkflow,
  draftData,
  predefinedSteps,
  createAndSend = false,
}: WorkflowModalProps) {
  const hasPredefinedSteps = !!predefinedSteps && predefinedSteps.length > 0;
  // Two-column (form + side panel) for edit/preview and community-step create;
  // plain create is a single comfortable column.
  const isTwoColumn = mode !== "create" || hasPredefinedSteps;
  const {
    isCreating,
    error: creationError,
    createWorkflow,
    clearError: clearCreationError,
  } = useWorkflowCreation();

  const { selectWorkflow } = useWorkflowSelection();

  // Get workflows store actions for optimistic updates
  const {
    addWorkflow: addToStore,
    updateWorkflow: updateInStore,
    removeWorkflow: removeFromStore,
    fetchWorkflows,
    invalidateCache,
  } = useWorkflowsStore();

  // Zustand UI state
  const {
    creationPhase,
    isGeneratingSteps,
    isRegeneratingSteps,
    isTogglingActivation,
    regenerationError,
    isActivated,
    setCreationPhase,
    setIsRegeneratingSteps,
    setIsTogglingActivation,
    setRegenerationError,
    setIsActivated,
    resetToForm,
  } = useWorkflowModalStore();

  const router = useRouter();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  // Single source of truth for workflow data
  const [currentWorkflow, setCurrentWorkflow] = useState<Workflow | null>(null);

  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch trigger schemas for slug normalization
  const { data: triggerSchemas } = useTriggerSchemas();

  const { integrations, connectIntegration } = useIntegrations();
  const [connectingId, setConnectingId] = useState<string | null>(null);

  // React Hook Form setup
  const form = useForm<WorkflowFormData>({
    resolver: zodResolver(workflowFormSchema),
    defaultValues: getDefaultFormValues(),
  });

  const {
    control,
    handleSubmit,
    reset: resetFormValues,
    setValue,
    watch,
    formState: { errors },
  } = form;

  // Defer the form reset until after the modal's exit animation finishes —
  // resetting synchronously on close blanks out the visible form fields while
  // the modal is still fading out, which reads as an abrupt close. The delay
  // matches HeroUI's modal exit transition.
  useEffect(() => {
    if (isOpen) return;
    const timer = globalThis.setTimeout(() => {
      // Only clear the form fields here — NOT the creation phase. Resetting the
      // phase to "form" mid-fade would flash the form/button view over the
      // terminal "success" screen on the way out. The phase is reset on open.
      resetFormValues(getDefaultFormValues());
    }, 250);
    return () => globalThis.clearTimeout(timer);
  }, [isOpen, resetFormValues]);

  // Reset the creation phase to a clean "form" when the modal OPENS (false ->
  // true transition only), so each session starts fresh without flashing the
  // previous session's success/error screen during the close animation. The ref
  // guard is essential: resetToForm/clearCreationError identities are unstable,
  // so without it this synchronous reset re-runs every render and infinite-loops.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      console.debug("[workflow:modal] opened -> resetting creation phase", {
        mode,
      });
      resetToForm();
      clearCreationError();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, resetToForm, clearCreationError, mode]);

  // Sync the local working copy from the prop only when a DIFFERENT workflow is
  // passed (modal opens / switches workflow). A background list refetch (e.g.
  // after save/regenerate) re-passes the SAME workflow with possibly-stale
  // steps; syncing on every object change would clobber freshly-regenerated
  // steps and flash the old ones. currentWorkflow is the edit-session truth.
  const syncedWorkflowIdRef = useRef<string | null>(null);
  useEffect(() => {
    const nextId = existingWorkflow?.id ?? null;
    if (nextId === syncedWorkflowIdRef.current) return;
    console.debug("[workflow:sync] syncing working copy from prop", {
      id: nextId,
      steps: existingWorkflow?.steps?.length ?? 0,
    });
    syncedWorkflowIdRef.current = nextId;
    setCurrentWorkflow(existingWorkflow ?? null);
  }, [existingWorkflow]);

  // Watch form data for change detection
  const formData = watch();

  // The integration slugs that step generation hints on (and that get persisted)
  // come from the @-mentions in the instructions. Falls back to the existing
  // workflow's saved slugs for older prompts that predate mention support.
  const selectedIntegrationSlugs = useMemo(() => {
    const mentioned = mentionedIntegrationIds(
      formData.prompt ?? "",
      integrations,
    );
    return mentioned.length > 0
      ? mentioned
      : (existingWorkflow?.integration_ids ?? []);
  }, [formData.prompt, integrations, existingWorkflow]);

  // The integration backing the selected event trigger, if it still needs
  // connecting. Resolved from the selected trigger slug (not trigger_config,
  // which can briefly lag the selection) so this banner always agrees with the
  // settings panel below — same slug, same integration. This one alone gates
  // saving: a trigger that can't fire makes the whole workflow inert.
  const missingTriggerIntegration = useMemo(() => {
    if (formData.activeTab !== "trigger" || !formData.selectedTrigger)
      return null;
    const integrationId = findTriggerSchema(
      triggerSchemas,
      formData.selectedTrigger,
    )?.integration_id;
    if (!integrationId) return null;
    const integration = integrations.find((i) => i.id === integrationId);
    return integration && integration.status !== "connected"
      ? integration
      : null;
  }, [
    formData.activeTab,
    formData.selectedTrigger,
    integrations,
    triggerSchemas,
  ]);

  // Integrations the generated steps require but the user hasn't connected.
  // Sourced from the backend-computed `missing_integrations` (same data the
  // workflow card uses) but re-checked against live connection status so a
  // freshly-connected integration drops out of the banner without a refetch.
  const missingStepIntegrations = useMemo<Integration[]>(() => {
    const refs = currentWorkflow?.missing_integrations ?? [];
    return refs
      .map((ref) => integrations.find((i) => i.id === ref.id))
      .filter((i): i is Integration => !!i && i.status !== "connected");
  }, [currentWorkflow, integrations]);

  // Trigger + step integrations that still need connecting, deduped by id.
  const missingIntegrations = useMemo<Integration[]>(() => {
    const byId = new Map<string, Integration>();
    if (missingTriggerIntegration)
      byId.set(missingTriggerIntegration.id, missingTriggerIntegration);
    for (const integration of missingStepIntegrations)
      byId.set(integration.id, integration);
    return [...byId.values()];
  }, [missingTriggerIntegration, missingStepIntegrations]);

  const handleConnectIntegration = useCallback(
    async (integrationId: string) => {
      if (connectingId) return;
      setConnectingId(integrationId);
      try {
        await connectIntegration(integrationId);
      } catch (err) {
        console.error("Failed to connect integration", err);
      } finally {
        setConnectingId(null);
      }
    },
    [connectingId, connectIntegration],
  );

  // Platform detection for keyboard shortcuts
  const { modifierKeyName } = usePlatform();

  // Check if save button should be disabled (used for hotkey and button)
  const isSaveDisabled = useCallback(() => {
    if (!formData.title.trim() || !formData.prompt?.trim()) {
      return true;
    }

    if (
      formData.activeTab === "schedule" &&
      formData.trigger_config.type === "schedule" &&
      !formData.trigger_config.cron_expression
    ) {
      // Schedule tab requires cron expression
      return true;
    }

    if (formData.activeTab === "trigger" && !formData.selectedTrigger) {
      // Trigger tab requires a trigger to be selected
      return true;
    }

    if (
      isIntegrationTrigger(formData.trigger_config) &&
      !hasValidTriggerName(formData.trigger_config)
    ) {
      // Integration triggers MUST have a valid trigger_name
      return true;
    }

    if (missingTriggerIntegration) {
      // Don't create/save a workflow whose trigger integration isn't connected
      return true;
    }

    if (mode === "edit" && !hasFormChanges()) {
      // Edit mode requires changes
      return true;
    }

    if (isCreating) {
      // Block while creating
      return true;
    }

    return false;
  }, [formData, mode, isCreating, existingWorkflow, missingTriggerIntegration]);

  // Keyboard shortcut: Escape to close modal
  useHotkeys(
    "escape",
    () => {
      if (isOpen && creationPhase === "form") {
        handleClose();
      }
    },
    { enableOnFormTags: true, enabled: isOpen && creationPhase === "form" },
    [isOpen, creationPhase],
  );

  // Keyboard shortcut: Mod+Enter to save
  useHotkeys(
    "mod+enter",
    () => {
      if (
        isOpen &&
        creationPhase === "form" &&
        mode !== "preview" &&
        !isSaveDisabled()
      ) {
        handleSubmit(handleSave)();
      }
    },
    {
      enableOnFormTags: true,
      enabled: isOpen && creationPhase === "form" && mode !== "preview",
    },
    [isOpen, creationPhase, isSaveDisabled, mode],
  );

  // Handle initial step generation (for empty workflows)
  const handleInitialGeneration = () => {
    handleRegenerateSteps("Generate workflow steps", false); // Don't force different tools for initial generation
  };

  // Initialize form data based on mode and currentWorkflow
  useEffect(() => {
    if ((mode === "edit" || mode === "preview") && currentWorkflow) {
      const formValues = workflowToFormData(currentWorkflow);
      resetFormValues(formValues);
      // Initialize activation state from current workflow
      setIsActivated(currentWorkflow.activated);
      // Reset to form phase for edit mode
      setCreationPhase("form");
      return;
    }

    // Handle draft data from AI-generated workflow
    if (mode === "create" && draftData) {
      resetFormValues(buildDraftFormValues(draftData, triggerSchemas));
      setIsActivated(true);
      setCreationPhase("form");
      return;
    }

    // Reset to default for create mode
    resetFormValues(getDefaultFormValues());
    setIsActivated(true);
    setCreationPhase("form");
  }, [
    mode,
    currentWorkflow,
    draftData,
    triggerSchemas,
    resetFormValues,
    setIsActivated,
    setCreationPhase,
  ]);

  // Check if form has actual changes for edit mode
  const hasFormChanges = () => {
    if (mode === "create") return true;

    if (!existingWorkflow) return true;

    const currentFormData = workflowToFormData(existingWorkflow);

    const persistedSlugs = [...(existingWorkflow.integration_ids ?? [])]
      .sort((a, b) => a.localeCompare(b))
      .join(",");
    const currentSlugs = [...selectedIntegrationSlugs]
      .sort((a, b) => a.localeCompare(b))
      .join(",");

    return (
      formData.title !== currentFormData.title ||
      formData.description !== currentFormData.description ||
      formData.prompt !== currentFormData.prompt ||
      formData.activeTab !== currentFormData.activeTab ||
      formData.selectedTrigger !== currentFormData.selectedTrigger ||
      JSON.stringify(formData.trigger_config) !==
        JSON.stringify(currentFormData.trigger_config) ||
      persistedSlugs !== currentSlugs
    );
  };

  // Create a brand-new workflow (optionally with predefined community steps).
  const handleCreate = async (data: WorkflowFormData) => {
    console.debug("[workflow:create] phase -> creating");
    setCreationPhase("creating");

    // Validate the trigger config before sending
    try {
      const validationResult = workflowFormSchema.safeParse(data);
      if (!validationResult.success) {
        setCreationPhase("error");
        return;
      }
    } catch (validationError) {
      console.error("Form validation error:", validationError);
      setCreationPhase("error");
      return;
    }

    // Create the request object that matches the backend API
    const createRequest = {
      title: data.title,
      description: data.description || undefined,
      prompt: data.prompt,
      trigger_config: data.trigger_config,
      // When predefined steps are supplied (from a community/featured
      // workflow), forward them so the backend reuses them instead of
      // regenerating a fresh plan.
      steps: hasPredefinedSteps
        ? predefinedSteps?.map((step) => ({
            id: step.id ?? "",
            title: step.title,
            description: step.description,
            category: step.category,
          }))
        : undefined,
      generate_immediately: !hasPredefinedSteps,
      notify_on_completion: data.notify_on_completion,
      integration_ids:
        selectedIntegrationSlugs.length > 0
          ? selectedIntegrationSlugs
          : undefined,
    };

    const result = await createWorkflow(createRequest);
    console.debug("[workflow:create] api returned", {
      success: result.success,
      id: result.workflow?.id,
      steps: result.workflow?.steps?.length ?? 0,
    });

    if (!result.success || !result.workflow) {
      setCreationPhase("error");
      return;
    }

    const createdWorkflow = result.workflow;
    trackEvent(ANALYTICS_EVENTS.WORKFLOWS_CREATED, {
      workflow_id: createdWorkflow.id,
      workflow_title: createdWorkflow.title,
      step_count: createdWorkflow.steps?.length || 0,
      trigger_type: data.trigger_config.type,
      has_schedule: data.trigger_config.type === "schedule",
    });

    // Update currentWorkflow with the newly created workflow
    setCurrentWorkflow(createdWorkflow);
    console.debug("[workflow:create] phase -> success");
    setCreationPhase("success");

    // Show success toast
    toast.success("Workflow created successfully!", {
      description: `${createdWorkflow.steps?.length || 0} steps generated`,
      duration: 3000,
    });

    // Optimistic update: add to store immediately for instant UI feedback
    addToStore(createdWorkflow);

    // Notify parent callbacks if provided (for backwards compatibility)
    if (onWorkflowSaved) onWorkflowSaved(createdWorkflow.id);
    invalidateCache();
    await fetchWorkflows();

    // In createAndSend mode, selectWorkflow navigates to /c and unmounts
    // this page (and modal). Closing here would push back to /workflows
    // and clobber that navigation, so only close when staying on the page.
    if (createAndSend) {
      selectWorkflow(createdWorkflow, { autoSend: true });
    } else {
      handleClose();
    }
  };

  // Regenerate steps after an edit that changed step-relevant fields. Keeps the
  // modal open with a visible indicator until the user dismisses it.
  const regenerateStepsAfterEdit = async (workflow: Workflow) => {
    console.debug(
      "[workflow:regen] step-relevant change detected, regenerating",
      {
        id: workflow.id,
      },
    );
    setIsRegeneratingSteps(true);
    setRegenerationError(null);
    try {
      const regenResult = await workflowApi.regenerateWorkflowSteps(
        workflow.id,
        {
          instruction: "Update steps to match the new workflow definition",
          force_different_tools: false,
          integration_ids:
            selectedIntegrationSlugs.length > 0
              ? selectedIntegrationSlugs
              : undefined,
        },
      );

      if (regenResult.workflow) {
        console.debug("[workflow:regen] api returned new steps", {
          id: workflow.id,
          steps: regenResult.workflow.steps?.length ?? 0,
        });
        // Commit the new steps locally AND to the store so the upcoming
        // fetchWorkflows() refetch can't briefly resurface the old steps.
        setCurrentWorkflow(regenResult.workflow);
        updateInStore(workflow.id, regenResult.workflow);
        toast.success("Workflow updated", {
          description: `${regenResult.workflow.steps?.length || 0} steps regenerated`,
          duration: 3000,
        });
      }
    } catch (regenError) {
      console.error("Failed to regenerate steps after update:", regenError);
      const message =
        regenError instanceof Error
          ? regenError.message
          : "Failed to regenerate steps";
      setRegenerationError(message);
      toast.error("Saved, but failed to regenerate steps", {
        description: message,
      });
    } finally {
      setIsRegeneratingSteps(false);
    }
  };

  // Persist edits to an existing workflow, regenerating steps if needed.
  const handleUpdate = async (data: WorkflowFormData) => {
    if (!currentWorkflow) return;

    try {
      const updateRequest = {
        title: data.title,
        description: data.description || undefined,
        prompt: data.prompt,
        trigger_config: {
          ...data.trigger_config,
        },
        notify_on_completion: data.notify_on_completion,
        integration_ids: selectedIntegrationSlugs,
      };

      // Decide if step regeneration is needed BEFORE persisting,
      // so the comparison runs against the previous truth.
      const previousFormData = workflowToFormData(currentWorkflow);
      const previousSlugs = [...(currentWorkflow.integration_ids ?? [])]
        .sort((a, b) => a.localeCompare(b))
        .join(",");
      const currentSlugs = [...selectedIntegrationSlugs]
        .sort((a, b) => a.localeCompare(b))
        .join(",");
      const stepRelevantChanged =
        data.prompt !== previousFormData.prompt ||
        data.description !== previousFormData.description ||
        JSON.stringify(data.trigger_config) !==
          JSON.stringify(previousFormData.trigger_config) ||
        previousSlugs !== currentSlugs;

      const updatedWorkflow = await workflowApi.updateWorkflow(
        currentWorkflow.id,
        updateRequest,
      );

      if (updatedWorkflow?.workflow) {
        setCurrentWorkflow(updatedWorkflow.workflow);
        updateInStore(currentWorkflow.id, updatedWorkflow.workflow);
      } else {
        updateInStore(currentWorkflow.id, updateRequest);
      }

      if (stepRelevantChanged) {
        await regenerateStepsAfterEdit(currentWorkflow);
      } else {
        toast.success("Workflow updated", { duration: 3000 });
      }

      if (onWorkflowSaved) onWorkflowSaved(currentWorkflow.id);

      invalidateCache();
      await fetchWorkflows();
    } catch (error) {
      console.error("Failed to update workflow:", error);
      toast.error("Failed to update workflow", {
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred. Please try again.",
        duration: 4000,
      });
    }
  };

  const handleSave = async (data: WorkflowFormData) => {
    if (!data.title.trim() || !data.prompt?.trim()) return;

    console.debug("[workflow:save] start", {
      mode,
      title: data.title,
      integrations: selectedIntegrationSlugs,
    });

    if (mode === "create") {
      await handleCreate(data);
      return;
    }

    // Edit mode - update the existing workflow
    await handleUpdate(data);
  };

  const handleClose = () => {
    // Reset is handled by the close-animation effect — calling it here would
    // blank the form while the modal is still visibly fading out.
    onOpenChange(false);
  };

  const handleResetToDefault = async () => {
    if (!existingWorkflow?.id) return;
    try {
      await workflowApi.resetToDefault(existingWorkflow.id);
      invalidateCache();
      await fetchWorkflows();
      handleClose();
    } catch (error) {
      toast.error("Failed to reset workflow", {
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        duration: 4000,
      });
    }
  };

  const handleDelete = () => {
    if (mode === "edit" && existingWorkflow) {
      setIsDeleteConfirmOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (!(mode === "edit" && existingWorkflow)) return;
    setIsDeleting(true);
    try {
      trackEvent(ANALYTICS_EVENTS.WORKFLOWS_DELETED, {
        workflow_id: existingWorkflow.id,
        workflow_title: existingWorkflow.title,
        step_count: existingWorkflow.steps?.length || 0,
        is_public: existingWorkflow.is_public,
      });

      await workflowApi.deleteWorkflow(existingWorkflow.id);
      removeFromStore(existingWorkflow.id);

      if (onWorkflowDeleted) onWorkflowDeleted(existingWorkflow.id);

      invalidateCache();
      await fetchWorkflows();
      setIsDeleteConfirmOpen(false);
      handleClose();
    } catch (error) {
      console.error("Failed to delete workflow:", error);
      toast.error("Failed to delete workflow", {
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred",
        duration: 4000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle activation toggle
  const handleActivationToggle = async (newActivated: boolean) => {
    if (mode !== "edit" || !currentWorkflow) return;

    // Block enabling a workflow whose trigger/steps need unconnected
    // integrations — it could never actually run.
    if (newActivated && missingIntegrations.length > 0) {
      toast.error("Can't enable this workflow", {
        description: missingIntegrationsMessage(missingIntegrations),
      });
      return;
    }

    setIsTogglingActivation(true);
    try {
      if (newActivated) {
        await workflowApi.activateWorkflow(currentWorkflow.id);
      } else {
        await workflowApi.deactivateWorkflow(currentWorkflow.id);
      }

      // Update currentWorkflow activation state
      setCurrentWorkflow({
        ...currentWorkflow,
        activated: newActivated,
      });
      setIsActivated(newActivated);
      updateInStore(currentWorkflow.id, { activated: newActivated });
      invalidateCache();
      await fetchWorkflows();
    } catch (error) {
      console.error("Failed to toggle workflow activation:", error);
    } finally {
      setIsTogglingActivation(false);
    }
  };

  // Handle step regeneration
  const handleRegenerateSteps = async (
    instruction: string = "Generate alternative workflow approach",
    forceDifferentTools: boolean = true,
  ) => {
    if (mode !== "edit" || !currentWorkflow) return;

    trackEvent(ANALYTICS_EVENTS.WORKFLOWS_STEPS_REGENERATED, {
      workflow_id: currentWorkflow.id,
      workflow_title: currentWorkflow.title,
      instruction,
      force_different_tools: forceDifferentTools,
      previous_step_count: currentWorkflow.steps?.length || 0,
    });

    setIsRegeneratingSteps(true);
    setRegenerationError(null);

    try {
      const result = await workflowApi.regenerateWorkflowSteps(
        currentWorkflow.id,
        {
          instruction,
          force_different_tools: forceDifferentTools,
          integration_ids:
            selectedIntegrationSlugs.length > 0
              ? selectedIntegrationSlugs
              : undefined,
        },
      );

      // Update workflow with new steps immediately
      if (result.workflow) {
        setCurrentWorkflow(result.workflow);

        toast.success("Steps regenerated successfully!", {
          description: `${result.workflow.steps?.length || 0} new steps created`,
          duration: 3000,
        });
      }

      if (onWorkflowSaved) onWorkflowSaved(currentWorkflow.id);
      invalidateCache();
      await fetchWorkflows();

      setIsRegeneratingSteps(false);
    } catch (error) {
      console.error("Failed to regenerate workflow steps:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to regenerate workflow steps";
      setRegenerationError(errorMessage);
      setIsRegeneratingSteps(false);
    }
  };

  const handlePublishToggle = async () => {
    if (!currentWorkflow?.id) return;
    try {
      if (currentWorkflow.is_public) {
        trackEvent(ANALYTICS_EVENTS.WORKFLOWS_UNPUBLISHED, {
          workflow_id: currentWorkflow.id,
          workflow_title: currentWorkflow.title,
        });
        await workflowApi.unpublishWorkflow(currentWorkflow.id);
        setCurrentWorkflow({ ...currentWorkflow, is_public: false });
      } else {
        trackEvent(ANALYTICS_EVENTS.WORKFLOWS_PUBLISHED, {
          workflow_id: currentWorkflow.id,
          workflow_title: currentWorkflow.title,
          step_count: currentWorkflow.steps?.length || 0,
        });
        const result = await workflowApi.publishWorkflow(currentWorkflow.id);
        const slug = result.slug ?? currentWorkflow.slug;
        setCurrentWorkflow({ ...currentWorkflow, is_public: true, slug });
        if (slug) router.push(`/use-cases/${slug}`);
      }
      invalidateCache();
      await fetchWorkflows();
    } catch (error) {
      console.error("Error publishing/unpublishing workflow:", error);
    }
  };

  const handleMarketplaceView = () => {
    if (!currentWorkflow?.slug) return;
    router.push(`/use-cases/${currentWorkflow.slug}`);
  };

  // Handle regeneration with a specific reason. The reason's instruction is the
  // single source of truth in constants/regeneration.ts (shared with the panel).
  const handleRegenerateWithReason = (instructionKey: string) => {
    const reason = REGENERATION_REASONS.find((r) => r.key === instructionKey);
    if (reason) {
      handleRegenerateSteps(reason.instruction, true); // Always force different tools
    }
  };

  // Handle workflow execution
  const handleRunWorkflow = async () => {
    if (mode !== "edit" || !existingWorkflow) return;

    // Check if workflow has steps before allowing execution
    if (!currentWorkflow?.steps || currentWorkflow.steps.length === 0) {
      toast.error("Cannot run workflow", {
        description:
          "This workflow doesn't have any steps generated yet. Please wait for step generation to complete.",
        duration: 4000,
      });
      return;
    }

    try {
      trackEvent(ANALYTICS_EVENTS.WORKFLOWS_EXECUTED, {
        workflow_id: existingWorkflow.id,
        workflow_title: existingWorkflow.title,
        step_count: currentWorkflow.steps.length,
        trigger_type: existingWorkflow.trigger_config.type,
      });

      // selectWorkflow navigates to /c, which unmounts this page (and modal).
      // Do NOT close the modal here: the parent's close handler pushes back to
      // /workflows, which would clobber the /c navigation in the same tick.
      selectWorkflow(existingWorkflow, { autoSend: true });
    } catch (error) {
      console.error("Failed to select workflow for execution:", error);
    }
  };

  const getButtonText = () => {
    if (mode === "edit") return isCreating ? "Saving..." : "Save";
    if (createAndSend) return isCreating ? "Creating..." : "Create and Send";
    return isCreating ? "Creating..." : "Create Workflow";
  };

  // Handle tab change for trigger section
  const handleActiveTabChange = (tab: "manual" | "schedule" | "trigger") => {
    setValue("activeTab", tab);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        hideCloseButton
        size={isTwoColumn ? "5xl" : "4xl"}
        // Two-column mode uses a definite height so the side panel's
        // flex/overflow chain (h-full → min-h-0 → overflow-y-auto) resolves
        // and the Steps panel doesn't clip.
        className={
          isTwoColumn
            ? "h-[85vh] max-h-208 max-w-6xl bg-secondary-bg"
            : "max-h-[90vh] bg-secondary-bg"
        }
        backdrop="blur"
      >
        <ModalContent>
          <ModalBody className="flex min-h-0 flex-col gap-0 p-0">
            {creationPhase === "form" ? (
              <>
                <div className="scrollbar-hover flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-6 pt-6 lg:flex-row lg:gap-8 lg:overflow-hidden">
                  {/* Form column — the single scroll region on desktop. Below
                      lg the row stacks and scrolls as a whole, so the column
                      keeps its full content height (shrink-0) instead of being
                      compressed by the fixed row height and overflowing onto the
                      panel below. lg:flex-1 restores fill-and-scroll on desktop. */}
                  <div className="flex min-h-0 shrink-0 flex-col lg:flex-1 lg:shrink lg:overflow-hidden">
                    <fieldset
                      disabled={mode === "preview"}
                      className="contents disabled:cursor-default"
                    >
                      <div className="scrollbar-hover space-y-8 pb-6 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-3">
                        <MissingIntegrationsAlert
                          missingIntegrations={missingIntegrations}
                          connectingId={connectingId}
                          onConnect={handleConnectIntegration}
                        />

                        <WorkflowHeader
                          mode={mode}
                          control={control}
                          errors={errors}
                          currentWorkflow={currentWorkflow}
                          isActivated={isActivated}
                          isTogglingActivation={isTogglingActivation}
                          onToggleActivation={handleActivationToggle}
                          isPublic={!!currentWorkflow?.is_public}
                          onUnpublish={handlePublishToggle}
                          onViewMarketplace={
                            currentWorkflow?.slug
                              ? handleMarketplaceView
                              : undefined
                          }
                          onDelete={handleDelete}
                          onResetToDefault={handleResetToDefault}
                        />

                        <WorkflowDescriptionField
                          control={control}
                          errors={errors}
                          setValue={setValue}
                          isPreview={mode === "preview"}
                          selectedIntegrationSlugs={selectedIntegrationSlugs}
                        />

                        <WorkflowTriggerSection
                          activeTab={formData.activeTab}
                          selectedTrigger={formData.selectedTrigger}
                          triggerConfig={formData.trigger_config}
                          onActiveTabChange={handleActiveTabChange}
                          onSelectedTriggerChange={(trigger) =>
                            setValue("selectedTrigger", trigger)
                          }
                          onTriggerConfigChange={(config) =>
                            setValue("trigger_config", config)
                          }
                          isPreview={mode === "preview"}
                        />

                        <div className="flex items-center justify-between gap-4 pb-1 pt-2">
                          <div className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium text-zinc-200">
                              Notify when runs finish
                            </span>
                            <span className="text-xs text-zinc-500">
                              GAIA shares the result in your channels after each
                              run
                            </span>
                          </div>
                          <Switch
                            size="sm"
                            isSelected={formData.notify_on_completion}
                            onValueChange={(enabled) =>
                              setValue("notify_on_completion", enabled, {
                                shouldDirty: true,
                              })
                            }
                            isDisabled={mode === "preview"}
                            aria-label="Notify when runs finish"
                          />
                        </div>
                      </div>
                    </fieldset>
                  </div>

                  {/* Side panel — predefined steps preview */}
                  {mode === "create" && hasPredefinedSteps && (
                    <div className="flex min-h-0 shrink-0 flex-col lg:w-[22rem] lg:pb-6">
                      <WorkflowStepsPreviewCard
                        steps={(predefinedSteps ?? []).map((step) => ({
                          id: step.id ?? "",
                          title: step.title,
                          description: step.description,
                          category: step.category,
                        }))}
                      />
                    </div>
                  )}

                  {/* Side panel — steps + history (edit / preview).
                      The column stretches to the row's (now definite) height and
                      the panel fills it via h-full, scrolling internally — so
                      switching Steps↔History can't resize the modal. `lg:pb-6`
                      matches the form column's bottom breathing room. */}
                  {(mode === "edit" || mode === "preview") &&
                    existingWorkflow && (
                      <fieldset
                        disabled={mode === "preview"}
                        className="flex min-h-0 shrink-0 flex-col disabled:cursor-default lg:w-88 lg:pb-6"
                      >
                        <WorkflowRightPanel
                          workflow={currentWorkflow}
                          workflowId={existingWorkflow.id}
                          isGenerating={isGeneratingSteps}
                          isRegenerating={isRegeneratingSteps}
                          regenerationError={regenerationError}
                          onRegenerateWithReason={handleRegenerateWithReason}
                          onInitialGeneration={handleInitialGeneration}
                          onClearError={() => setRegenerationError(null)}
                          isPreview={mode === "preview"}
                        />
                      </fieldset>
                    )}
                </div>

                {/* Full-width footer */}
                <div className="shrink-0">
                  <Divider className="bg-zinc-700" />
                  <div className="px-6 py-4">
                    {mode === "preview" ? (
                      <PreviewFooter onClose={handleClose} />
                    ) : (
                      <WorkflowFooter
                        existingWorkflow={!!existingWorkflow}
                        hasSteps={
                          !!currentWorkflow?.steps &&
                          currentWorkflow.steps.length > 0
                        }
                        onRunWorkflow={handleRunWorkflow}
                        onCancel={handleClose}
                        onSave={() => handleSubmit(handleSave)()}
                        isSaveDisabled={isSaveDisabled()}
                        isCreating={isCreating}
                        modifierKeyName={modifierKeyName}
                        buttonText={getButtonText()}
                        isPublic={!!currentWorkflow?.is_public}
                        onPublish={handlePublishToggle}
                      />
                    )}
                  </div>
                </div>
              </>
            ) : (
              <div className="px-6 py-4">
                <WorkflowLoadingState
                  phase={creationPhase}
                  mode={mode}
                  error={creationError}
                  workflow={currentWorkflow}
                  onClose={handleClose}
                  onRetry={() => setCreationPhase("form")}
                />
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      {mode === "edit" && existingWorkflow && (
        <ConfirmationDialog
          isOpen={isDeleteConfirmOpen}
          title="Delete workflow"
          message={`Are you sure you want to delete "${existingWorkflow.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="destructive"
          isLoading={isDeleting}
          onConfirm={confirmDelete}
          onCancel={() => setIsDeleteConfirmOpen(false)}
        />
      )}
    </>
  );
}

function PreviewFooter({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-2 text-xs text-zinc-400">
        <InformationCircleIcon height={16} className="shrink-0 text-zinc-500" />
        <span>
          You can customise every detail later from the Workflows page.
        </span>
      </div>
      <Button color="primary" onPress={onClose}>
        Close
      </Button>
    </div>
  );
}
